import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../components/Navbar";

// Professional icon components
const WebIcon = () => (
  <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
  </svg>
);

const MobileIcon = () => (
  <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

const MarketingIcon = () => (
  <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const CloudIcon = () => (
  <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

const DesignIcon = () => (
  <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM7 21h10a2 2 0 002-2v-4a2 2 0 00-2-2H7" />
  </svg>
);

const AnalyticsIcon = () => (
  <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const ChevronIcon = ({ isOpen }) => (
  <svg
    className={`w-5 h-5 text-red-700 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const services = [
  {
    title: "Web Development",
    description:
      "Enterprise-grade web applications built with modern frameworks and best practices. We deliver scalable, secure, and high-performance solutions that drive business growth.",
    icon: <WebIcon />,
    color: "from-red-600 to-red-800",
    features: ["React & Next.js", "Node.js Backend", "Cloud Deployment", "API Integration"],
  },
  {
    title: "Mobile App Development",
    description:
      "Native iOS and Android applications with seamless user experiences. Our development process ensures optimal performance, security, and maintainability.",
    icon: <MobileIcon />,
    color: "from-red-700 to-red-900",
    features: ["Native iOS/Android", "Cross-platform", "App Store Optimization", "Push Notifications"],
  },
  {
    title: "Digital Marketing",
    description:
      "Data-driven marketing strategies that deliver measurable results. From SEO to paid advertising, we help businesses reach and convert their target audience.",
    icon: <MarketingIcon />,
    color: "from-red-600 to-red-800",
    features: ["SEO & SEM", "Social Media", "Content Marketing", "Analytics & Reporting"],
  },
  {
    title: "Cloud Solutions",
    description:
      "Comprehensive cloud infrastructure and migration services. We help businesses leverage cloud computing for improved scalability, security, and cost-efficiency.",
    icon: <CloudIcon />,
    color: "from-red-700 to-red-900",
    features: ["AWS & Azure", "Cloud Migration", "DevOps", "Security & Compliance"],
  },
  {
    title: "UI/UX Design",
    description:
      "User-centered design solutions that enhance engagement and conversion. Our design process is rooted in research, testing, and iterative improvement.",
    icon: <DesignIcon />,
    color: "from-red-600 to-red-800",
    features: ["User Research", "Wireframing", "Prototyping", "Design Systems"],
  },
  {
    title: "Data Analytics",
    description:
      "Transform your data into actionable insights with advanced analytics solutions. We help businesses make informed decisions through comprehensive data analysis.",
    icon: <AnalyticsIcon />,
    color: "from-red-700 to-red-900",
    features: ["Business Intelligence", "Data Visualization", "Machine Learning", "Predictive Analytics"],
  },
];

/* ─────────────────────────────────────────
   Mobile Accordion Card (< md screens)
───────────────────────────────────────── */
const MobileServiceCard = ({ service, index }) => {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.07 }}
      className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden"
    >
      {/* Accordion Header — always visible */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 p-5 text-left focus:outline-none active:bg-gray-50 transition-colors"
        aria-expanded={open}
      >
        {/* Icon */}
        <div
          className={`w-11 h-11 flex-shrink-0 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center text-white shadow-md`}
        >
          {service.icon}
        </div>

        {/* Title */}
        <span className="flex-1 text-base font-heading text-gray-900">{service.title}</span>

        {/* Chevron */}
        <ChevronIcon isOpen={open} />
      </button>

      {/* Expandable body */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t border-gray-100">
              <p className="text-gray-600 font-mono-body text-sm leading-relaxed mt-4 mb-4">
                {service.description}
              </p>

              <div className="grid grid-cols-2 gap-x-3 gap-y-2 mb-5">
                {service.features.map((feature, i) => (
                  <div key={i} className="flex items-center text-xs text-gray-700">
                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2 flex-shrink-0" />
                    <span className="font-mono-body">{feature}</span>
                  </div>
                ))}
              </div>

              <a
                href="#"
                className="inline-flex items-center text-red-700 font-heading text-sm hover:text-red-900 transition-colors"
              >
                Learn More
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ─────────────────────────────────────────
   Desktop Card (≥ md screens)
───────────────────────────────────────── */
const DesktopServiceCard = ({ service }) => (
  <div className="service-card bg-white rounded-2xl shadow-lg border border-gray-100 p-8 backdrop-blur-sm bg-opacity-90 flex flex-col">
    <div
      className={`w-16 h-16 rounded-xl bg-gradient-to-r ${service.color} flex items-center justify-center text-white mb-6 shadow-lg`}
    >
      {service.icon}
    </div>

    <h3 className="text-2xl font-heading text-gray-900 mb-4">{service.title}</h3>

    <p className="text-gray-600 mb-6 leading-relaxed font-mono-body text-sm flex-1">
      {service.description}
    </p>

    <div className="space-y-3 mb-6">
      {service.features.map((feature, i) => (
        <div key={i} className="flex items-center text-sm text-gray-700">
          <div className="feature-dot w-2 h-2 bg-red-600 rounded-full mr-3 flex-shrink-0" />
          <span className="font-mono-body">{feature}</span>
        </div>
      ))}
    </div>

    <div className="pt-6 border-t border-gray-100 mt-auto">
      <a
        href="#"
        className="inline-flex items-center text-red-700 hover:text-red-900 font-heading text-sm transition-colors duration-200"
      >
        Learn More
        <svg className="w-4 h-4 ml-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </a>
    </div>
  </div>
);

/* ─────────────────────────────────────────
   Main Page
───────────────────────────────────────── */
const Services = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&family=JetBrains+Mono:wght@400;500&display=swap');

        .font-display  { font-family: 'Inter', sans-serif; font-weight: 900; }
        .font-mono-body{ font-family: 'JetBrains Mono', monospace; font-weight: 400; }
        .font-heading  { font-family: 'Inter', sans-serif; font-weight: 700; }

        /* ── Desktop card hover ── */
        .service-card {
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          position: relative;
          overflow: hidden;
        }
        .service-card::before {
          content: '';
          position: absolute;
          top: 0; left: -100%;
          width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(139,0,0,0.05), transparent);
          transition: left 0.6s;
        }
        @media (hover: hover) {
          .service-card:hover::before { left: 100%; }
          .service-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 40px rgba(139,0,0,0.15);
          }
          .service-card:hover .feature-dot {
            background-color: #00538b;
            transform: scale(1.3);
          }
        }

        /* ── CTA button shimmer ── */
        .cta-button {
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .cta-button::before {
          content: '';
          position: absolute;
          top: 0; left: -100%;
          width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s;
        }
        @media (hover: hover) {
          .cta-button:hover::before { left: 100%; }
        }

        .feature-dot { transition: all 0.3s ease; }

        /* ── Fluid heading on tiny screens ── */
        @media (max-width: 640px) {
          .fluid-heading { font-size: clamp(1.75rem, 9vw, 3rem); }
        }

        /* ── Safe-area padding for notched phones ── */
        .safe-section {
          padding-left:  max(1rem, env(safe-area-inset-left));
          padding-right: max(1rem, env(safe-area-inset-right));
          padding-bottom: max(3rem, env(safe-area-inset-bottom));
        }
      `}</style>

      <Navbar />

      <section className="safe-section relative min-h-screen py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">

        {/* Decorative Wave Pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <svg className="absolute top-0 left-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="wave-pattern-services" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <path d="M0 50 Q 25 25, 50 50 T 100 50" stroke="#8b0000" strokeWidth="0.5" fill="none" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#wave-pattern-services)" />
          </svg>
        </div>

        {/* Gradient Orbs — hidden on very small screens for perf */}
        <div className="hidden xs:block absolute top-40 right-20 w-72 h-72 lg:w-96 lg:h-96 bg-red-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" />
        <div className="hidden xs:block absolute bottom-40 left-20 w-72 h-72 lg:w-96 lg:h-96 bg-red-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: "2s" }} />

        <div className="max-w-7xl mx-auto relative z-10">

          {/* ── Header ── */}
          <div className="text-center mb-10 sm:mb-14 md:mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Badge */}
              <div className="inline-block mb-4 sm:mb-6">
                <span className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-red-50 text-red-700 text-xs sm:text-sm font-heading border border-red-200">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                  </svg>
                  Our Expertise
                </span>
              </div>

              <h1 className="fluid-heading text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-display text-gray-900 mb-4 sm:mb-6 leading-tight px-2">
                Our{" "}
                <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
                  Services
                </span>
              </h1>

              <div className="w-20 sm:w-32 h-1 sm:h-1.5 bg-gradient-to-r from-red-600 to-red-800 mx-auto mb-5 sm:mb-8 rounded-full" />

              <p className="text-sm sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-mono-body px-2">
                We provide comprehensive technology solutions designed to help your business succeed in the digital age. Our team of experts delivers results that matter.
              </p>
            </motion.div>
          </div>

          {/* ── Services: Accordion on mobile / Grid on desktop ── */}

          {/* Mobile accordion (hidden on md+) */}
          <div className="flex flex-col gap-3 md:hidden mb-10">
            {services.map((service, index) => (
              <MobileServiceCard key={index} service={service} index={index} />
            ))}
          </div>

          {/* Desktop grid (hidden below md) */}
          <motion.div
            className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16 md:mb-20"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {services.map((service, index) => (
              <motion.div key={index} variants={itemVariants}>
                <DesktopServiceCard service={service} />
              </motion.div>
            ))}
          </motion.div>

          {/* ── Call to Action ── */}
          <motion.div
            className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100 p-6 sm:p-10 md:p-16 backdrop-blur-sm bg-opacity-90"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="text-center max-w-4xl mx-auto">
              <h3 className="text-2xl sm:text-3xl md:text-5xl font-display text-gray-900 mb-3 sm:mb-6 leading-tight px-2">
                Ready to Get Started?
              </h3>
              <p className="text-sm sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-10 font-mono-body leading-relaxed px-2">
                Let's discuss how we can help transform your business with our expert services. Contact us today for a free consultation.
              </p>

              <div className="flex flex-col xs:flex-row gap-3 sm:gap-4 justify-center px-2">
                <motion.button
                  className="cta-button bg-[#8b0000] hover:bg-[#6b0000] text-white px-6 sm:px-10 py-3 sm:py-4 rounded-lg font-heading text-sm sm:text-lg shadow-lg w-full xs:w-auto"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Get Free Consultation
                </motion.button>

                <motion.button
                  className="border-2 border-[#8b0000] text-[#8b0000] px-6 sm:px-10 py-3 sm:py-4 rounded-lg font-heading text-sm sm:text-lg hover:bg-red-50 transition-colors duration-200 w-full xs:w-auto"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View Our Portfolio
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* ── Stats ── */}
          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 lg:gap-8 mt-10 sm:mt-14 md:mt-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {[
              { number: "100+", label: "Projects Completed" },
              { number: "96%",  label: "Client Satisfaction" },
              { number: "10+",  label: "Expert Team Members" },
              { number: "24/7", label: "Support Available" },
            ].map((stat, index) => (
              <div
                key={index}
                className="text-center bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-100"
              >
                <div className="text-2xl sm:text-3xl lg:text-4xl font-display text-[#8b0000] mb-1 sm:mb-2">
                  {stat.number}
                </div>
                <div className="text-xs sm:text-sm text-gray-600 font-mono-body leading-snug">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>

        </div>
      </section>
    </>
  );
};

export default Services;